/* eslint-disable @next/next/no-img-element */
'use client'

import { aspectRatioToPaddingBottom, DEFAULT_SLIDER_ASPECT_RATIO, type TileAspectRatio } from '@/components/scenario/aspectRatio'
import UnifiedPlayMark from '@/components/embedding/UnifiedPlayMark'
import { DEFAULT_THUMBNAIL_ROUNDED } from '@/components/scenario/thumbnailCorner'
import { useHoverPrefetch } from '@/hooks/useHoverPrefetch'
import { createThumbnailHoverHandlers } from '@/components/video/HoverAnimatedThumbnail'
import { ScenarioNodeWithVideoUrl } from '@/orval/generated/public/model'
import React, { FC } from 'react'

export interface PCSliderGridProps {
  orderedNodes: ScenarioNodeWithVideoUrl[]
  visibleNodes: ScenarioNodeWithVideoUrl[]
  currentIndex: number
  isTransitioning: boolean
  slideDirection: 'left' | 'right' | null
  onPrevious: () => void
  onNext: () => void
  onThumbnailClick: (index: number) => void
  aspectRatio?: TileAspectRatio
  /** サムネイルの角丸。false なら直角（先方サイトのデザインに合わせる設定） */
  thumbnailRounded?: boolean
}

const arrowStyle: React.CSSProperties = { background: 'white', color: '#9ca3af', border: '1px solid rgb(156, 163, 175)', width: '64px', height: '64px' }
const arrowHoverIn = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = '#6b7280'; e.currentTarget.style.color = '#4b5563' }
const arrowHoverOut = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.color = '#9ca3af' }

const PCSliderGrid: FC<PCSliderGridProps> = ({
  orderedNodes,
  visibleNodes,
  currentIndex,
  isTransitioning,
  slideDirection,
  onPrevious,
  onNext,
  onThumbnailClick,
  aspectRatio = DEFAULT_SLIDER_ASPECT_RATIO,
  thumbnailRounded = DEFAULT_THUMBNAIL_ROUNDED,
}) => {
  const { schedulePrefetch, cancelPrefetch } = useHoverPrefetch()
  // Tailwind のビルド時スキャンに拾わせるため、クラス名はリテラルで分岐させる
  const cornerClass = thumbnailRounded ? 'rounded-2xl' : 'rounded-none'
  return (
    <>
      <style>{`
        @keyframes slideInFromLeft {
          0% { opacity: 0; transform: translateX(-50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInFromRight {
          0% { opacity: 0; transform: translateX(50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        /* A8: Removed blur effect, using opacity-only transition */
        .thumbnail-grid-transitioning { opacity: 0.7; }
        .thumbnail-grid-transitioning .thumbnail-card { box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); }
        .thumbnail-card .play-overlay {
          opacity: 1 !important;
          background: transparent;
        }
        .thumbnail-card:hover .play-overlay {
          background: rgba(0, 0, 0, 0.3);
        }
        .thumbnail-card .play-icon {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          border: 1px solid rgba(255, 255, 255, 0.25);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          transition: transform 200ms ease;
        }
        .thumbnail-card:hover .play-icon {
          transform: scale(1.35);
          backdrop-filter: blur(0px) !important;
          -webkit-backdrop-filter: blur(0px) !important;
        }
      `}</style>

      <div className="relative flex items-center gap-4">
        {orderedNodes.length > 3 && (
          <button
            type="button"
            className="flex-shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ease-out hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed" style={arrowStyle} onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}
            onClick={onPrevious}
            disabled={isTransitioning}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          </button>
        )}

        <div
          className={`grid grid-cols-3 gap-4 flex-1 relative overflow-hidden transition-opacity duration-200 ease-out ${isTransitioning ? 'thumbnail-grid-transitioning' : ''}`}
        >
          {visibleNodes.map((node, index) => {
            const animation =
              isTransitioning && slideDirection
                ? slideDirection === 'left'
                  ? 'slideInFromRight 0.2s ease-out'
                  : 'slideInFromLeft 0.2s ease-out'
                : 'none'

            return (
              <div
                key={node.id}
                className={`thumbnail-card group cursor-pointer ${cornerClass} overflow-hidden bg-gray-100 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_10px_25px_rgba(0,0,0,0.2)]`}
                style={{ animation }}
                onClick={() => onThumbnailClick(index)}
                onPointerEnter={() => schedulePrefetch(node.videoUrl)}
                onPointerLeave={cancelPrefetch}
              >
                <div className={`relative w-full overflow-hidden bg-black ${cornerClass}`} style={{ paddingBottom: aspectRatioToPaddingBottom(aspectRatio) }}>
                  {node.thumbnailUrl ? (
                    <img
                      src={node.thumbnailUrl}
                      alt={`動画 ${currentIndex + index + 1}`}
                      className="absolute top-0 left-0 w-full h-full object-cover"
                      {...createThumbnailHoverHandlers(node.thumbnailUrl, node.animatedThumbnailUrl)}
                    />
                  ) : (
                    <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-gray-200">
                      <div className="text-5xl text-gray-600">&#9654;</div>
                    </div>
                  )}
                  <div className="play-overlay pointer-events-none absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-0 transition-all duration-200">
                    <UnifiedPlayMark />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {orderedNodes.length > 3 && (
          <button
            type="button"
            className="flex-shrink-0 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ease-out hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed" style={arrowStyle} onMouseEnter={arrowHoverIn} onMouseLeave={arrowHoverOut}
            onClick={onNext}
            disabled={isTransitioning}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </button>
        )}
      </div>
    </>
  )
}

export default PCSliderGrid
